import { ButtonHTMLAttributes, ReactNode, forwardRef } from "react";
import cn from "classnames";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?:
    | "primary"
    | "secondary"
    | "clean"
    | "outline"
    | "danger"
    | "success"
    | "warning"
    | "tab"
    | "ghost"
    // Marketing-style primary with a blue glow. For hero / CTA surfaces
    // (Academy, paywall), not for in-app toolbars.
    | "glow";
  size?: "xs" | "sm" | "md" | "lg";
  loading?: boolean;
  selected?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      disabled,
      variant = "clean",
      size = "md",
      selected = false,
      loading,
      ...props
    },
    ref
  ) => {
    function buttonStyleColors() {
      switch (variant) {
        case "primary":
          return "bg-blue-500 hover:bg-blue-600 text-white";
        case "secondary":
          return "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60";
        case "danger":
          return "border-[1px] border-red-300 dark:border-red-900 shadow-none bg-red-50 dark:bg-red-950/40 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/60";
        case "success":
          return "shadow-none bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-950/60";
        case "warning":
          return "shadow-none bg-yellow-100 dark:bg-amber-950/40 text-yellow-700 dark:text-amber-400 hover:bg-yellow-200 dark:hover:bg-amber-950/60";
        case "outline":
          return "border-[1px] border-gray-300 dark:border-base-300 shadow-none hover:bg-blue-50 dark:hover:bg-blue-900/40 hover:border-gray-300 dark:hover:border-base-content/30 hover:border-blue-200 dark:hover:border-blue-800";
        case "tab":
          return cn(
            // border-color excluded from the transition so the hovered
            // underline snaps to the same blue as the selected tab instead of
            // fading through darker shades.
            "border-b-2 shadow-none rounded-none hover:bg-blue-50 dark:hover:bg-blue-900/40 transition-[background-color,color]",
            selected
              ? // hover:border repeated so it outranks DaisyUI's .btn:hover
                // border-color, which otherwise darkens the underline.
                "text-blue-500 dark:text-blue-300 border-blue-500 hover:border-blue-500"
              : "text-base-content/40 hover:text-base-content/60 border-gray-400 dark:border-base-content/40 hover:border-blue-500 dark:hover:border-blue-500"
          );
        case "ghost":
          return "shadow-none bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60";
        case "glow":
          return "bg-blue-500 hover:bg-blue-600 text-white rounded-btn shadow-glow hover:shadow-glow-hover hover:-translate-y-px transition-[box-shadow,transform,background-color]";
        case "clean":
          return "shadow-none";
        default:
          break;
      }
    }

    function buttonSizes() {
      switch (size) {
        case "xs":
          return "!min-h-7 h-[20px] px-2 rounded-lg text-xs";
        case "sm":
          return "!min-h-10 h-[25px] text-xs xl:text-sm px-2";
        case "lg":
          return "!min-h-14 h-[30px]";
        case "md":
          return "text-xs xl:text-sm px-0 xl:px-4";
        default:
          break;
      }
    }

    return (
      <button
        ref={ref}
        className={cn(
          "btn no-animation border-0 inline-flex items-center gap-1",
          // A disabled button is grey, full stop: the variant colours are
          // skipped rather than overlaid, so a red "danger" action can't
          // still look live before its confirmation is met.
          disabled
            ? "!bg-gray-400 dark:!bg-base-300 !text-gray-200 dark:!text-base-content/40 border-transparent shadow-none pointer-events-none cursor-not-allowed"
            : buttonStyleColors(),
          buttonSizes(),
          className
        )}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
